

/**
 * SAWSEE — Shop by
 * Size / Colour / Product Type
 *
 * Desktop:
 * - Works as a normal tab interface
 * - One panel is always open
 *
 * Mobile:
 * - Collapsed by default
 * - Tap a tab to expand its panel
 * - Tap the active tab again to collapse
 * - Only one panel can be open at a time
 *
 * Filtering itself is NOT handled here.
 * Shopify's existing <facet-filters-form> / facets.js handles:
 * - AJAX filtering
 * - URL parameters
 * - product grid refresh
 * - pagination
 * - sorting
 *
 * Event delegation is used because Shopify replaces
 * #ProductGridContainer markup after every filter update.
 */
 
(function () {
  'use strict';
 
  var MOBILE_BREAKPOINT = 749;
 
  /*
   * Desktop state:
   * remembers last active tab.
   */
  var currentDesktopTab = null;
 
  /*
   * Mobile state:
   * null = collapsed
   * otherwise contains "size", "colour", or "type"
   */
  var currentMobileTab = null;
 
 
  /* ==========================================================
     HELPERS
     ========================================================== */
 
  function isMobile() {
    return window.matchMedia(
      '(max-width: ' + MOBILE_BREAKPOINT + 'px)'
    ).matches;
  }
 
 
  function getFirstTab(root) {
    var firstButton = root.querySelector('.shopby__tab');
 
    return firstButton
      ? firstButton.getAttribute('data-shopby-tab')
      : null;
  }
 
 
  function getMarkupDefaultTab(root) {
    var activeButton =
      root.querySelector('.shopby__tab.is-active') ||
      root.querySelector('.shopby__tab');
 
    return activeButton
      ? activeButton.getAttribute('data-shopby-tab')
      : null;
  }
 
 
  function tabExists(root, tab) {
    if (!root || !tab) {
      return false;
    }
 
    return !!root.querySelector(
      '.shopby__tab[data-shopby-tab="' + tab + '"]'
    );
  }
 
 
  /* ==========================================================
     DESKTOP
     ========================================================== */
 
  function setDesktopTab(root, tab) {
    if (!root) {
      return;
    }
 
    if (!tabExists(root, tab)) {
      tab = getMarkupDefaultTab(root);
    }
 
    if (!tab) {
      return;
    }
 
    currentDesktopTab = tab;
 
    root
      .querySelectorAll('.shopby__tab')
      .forEach(function (button) {
        var buttonTab =
          button.getAttribute('data-shopby-tab');
 
        var active = buttonTab === tab;
 
        button.classList.toggle(
          'is-active',
          active
        );
 
        button.setAttribute(
          'aria-selected',
          active ? 'true' : 'false'
        );
 
        button.setAttribute(
          'aria-expanded',
          active ? 'true' : 'false'
        );
      });
 
 
    root
      .querySelectorAll('[data-shopby-panel]')
      .forEach(function (panel) {
        var panelTab =
          panel.getAttribute('data-shopby-panel');
 
        panel.hidden = panelTab !== tab;
      });
  }
 
 
  /* ==========================================================
     MOBILE
     ========================================================== */
 
  function collapseMobile(root) {
    if (!root) {
      return;
    }
 
    currentMobileTab = null;
 
    root
      .querySelectorAll('.shopby__tab')
      .forEach(function (button) {
        button.classList.remove('is-active');
 
        button.setAttribute(
          'aria-selected',
          'false'
        );
 
        button.setAttribute(
          'aria-expanded',
          'false'
        );
      });
 
 
    root
      .querySelectorAll('[data-shopby-panel]')
      .forEach(function (panel) {
        panel.hidden = true;
      });
 
    root.classList.remove('shopby--open');
  }
 
 
  function openMobileTab(root, tab) {
    if (!root || !tabExists(root, tab)) {
      return;
    }
 
    currentMobileTab = tab;
 
    root.classList.add('shopby--open');
 
    root
      .querySelectorAll('.shopby__tab')
      .forEach(function (button) {
        var buttonTab =
          button.getAttribute('data-shopby-tab');
 
        var active = buttonTab === tab;
 
        button.classList.toggle(
          'is-active',
          active
        );
 
        button.setAttribute(
          'aria-selected',
          active ? 'true' : 'false'
        );
 
        button.setAttribute(
          'aria-expanded',
          active ? 'true' : 'false'
        );
      });
 
 
    root
      .querySelectorAll('[data-shopby-panel]')
      .forEach(function (panel) {
        var panelTab =
          panel.getAttribute('data-shopby-panel');
 
        panel.hidden = panelTab !== tab;
      });
  }
 
 
  function toggleMobileTab(root, tab) {
    if (!root || !tab) {
      return;
    }
 
    /*
     * Clicking the currently open tab collapses it.
     */
    if (currentMobileTab === tab) {
      collapseMobile(root);
      return;
    }
 
    /*
     * Otherwise open the selected tab
     * and automatically close the previous one.
     */
    openMobileTab(root, tab);
  }
 
 
  /* ==========================================================
     APPLY CURRENT STATE
     ========================================================== */
 
  function applyState(root) {
    if (!root) {
      return;
    }
 
    if (isMobile()) {
 
      /*
       * On the first mobile load,
       * keep everything collapsed.
       */
      if (!currentMobileTab) {
        collapseMobile(root);
        return;
      }
 
      /*
       * If AJAX changed available tabs,
       * don't try to restore a missing tab.
       */
      if (!tabExists(root, currentMobileTab)) {
        collapseMobile(root);
        return;
      }
 
      openMobileTab(
        root,
        currentMobileTab
      );
 
      return;
    }
 
 
    /*
     * Desktop
     */
 
    var desktopTab =
      currentDesktopTab ||
      getMarkupDefaultTab(root) ||
      getFirstTab(root);
 
    setDesktopTab(
      root,
      desktopTab
    );
  }
 
 
  function initAll() {
    document
      .querySelectorAll('.shopby')
      .forEach(function (root) {
        applyState(root);
      });
  }
 
 
  /* ==========================================================
     PIN THE OPEN TAB TO WHATEVER VALUE WAS JUST PICKED
 
     Picking a value (a colour swatch, a size pill, a product type
     pill) submits the facet form, which — via Shopify's existing
     AJAX pipeline — replaces this whole widget with a freshly
     server-rendered copy. That fresh copy doesn't know which panel
     the shopper had open; only currentMobileTab / currentDesktopTab
     (this script's own memory) does.
 
     Previously, nothing updated that memory when a *value* changed —
     only clicking a tab button did. So if anything caused the wrong
     tab to end up open after a rebuild (any mismatch in the
     server-rendered default state, for instance), there was nothing
     to correct it back to the panel the shopper was actually using.
 
     Recording the tab here, synchronously, the instant the input
     changes — before the AJAX request is even sent — means the
     panel that reopens after every rebuild is always the one whose
     value the shopper just picked, regardless of what the
     server-rendered markup says.
     ========================================================== */
 
  document.addEventListener(
    'input',
    function (event) {
 
      var input =
        event.target.closest('.shopby-input');
 
      if (!input) {
        return;
      }
 
      var root =
        input.closest('.shopby');
 
      if (!root) {
        return;
      }
 
      var panel =
        input.closest('[data-shopby-panel]');
 
      if (!panel) {
        return;
      }
 
      var tab =
        panel.getAttribute('data-shopby-panel');
 
      if (!tab) {
        return;
      }
 
      if (isMobile()) {
        currentMobileTab = tab;
      } else {
        currentDesktopTab = tab;
      }
    }
  );
 
 
  /* ==========================================================
     RESET — COLLAPSE ON MOBILE
 
     Reset clears Size, Colour and Product Type together and, like
     every other value change, rebuilds this whole widget through
     Shopify's AJAX pipeline. Left alone, the "pin the tab to
     whatever was just picked" behaviour above would kick in for the
     wrong reason: it would keep whichever panel the shopper had
     open, now showing nothing selected, instead of the bar reading
     as genuinely reset.
 
     This collapses the panel immediately and synchronously, right
     here on click — it does NOT just clear currentMobileTab and wait
     for the AJAX rebuild's MutationObserver to reapply state.
     Waiting on the rebuild means the collapse only happens if that
     rebuild actually lands and re-triggers applyState() the same way
     every time — and a same-URL Reset (e.g. resetting a Colour
     filter a second time) can be served from FacetFiltersForm's own
     response cache instead of a fresh fetch, which still replaces
     the markup but doesn't guarantee the same observable timing.
     Collapsing here removes that dependency entirely: the accordion
     is already shut before any of that happens, and whatever markup
     lands afterwards has nothing to fight it on.
 
     Reset means "start over," and the bar's own starting state (on
     first load) is collapsed — so this makes every Reset tap, not
     just the first, snap the accordion shut instead of leaving an
     empty panel open.
 
     Desktop is untouched: it has no collapsed state to return to, so
     the currently open tab simply shows as cleared, same as before.
 
     Capture phase (the trailing `true`) so this always runs, even if
     something else in the click chain were to stop the event from
     bubbling to document.
     ========================================================== */
 
  document.addEventListener(
    'click',
    function (event) {
 
      var resetLink =
        event.target.closest('.shopby__reset');
 
      if (!resetLink) {
        return;
      }
 
      var root =
        resetLink.closest('.shopby');
 
      if (!root) {
        return;
      }
 
      if (!isMobile()) {
        return;
      }
 
      collapseMobile(root);
    },
    true
  );
 
 
  /* ==========================================================
     TAB CLICK
     ========================================================== */
 
  document.addEventListener(
    'click',
    function (event) {
 
      var button =
        event.target.closest('.shopby__tab');
 
      if (!button) {
        return;
      }
 
      var root =
        button.closest('.shopby');
 
      if (!root) {
        return;
      }
 
      var tab =
        button.getAttribute(
          'data-shopby-tab'
        );
 
      if (!tab) {
        return;
      }
 
      event.preventDefault();
 
 
      if (isMobile()) {
 
        toggleMobileTab(
          root,
          tab
        );
 
      } else {
 
        setDesktopTab(
          root,
          tab
        );
 
      }
    }
  );
 
 
  /* ==========================================================
     KEYBOARD ACCESSIBILITY
     ========================================================== */
 
  document.addEventListener(
    'keydown',
    function (event) {
 
      var button =
        event.target.closest('.shopby__tab');
 
      if (!button) {
        return;
      }
 
      if (
        event.key !== 'Enter' &&
        event.key !== ' '
      ) {
        return;
      }
 
      event.preventDefault();
 
      button.click();
    }
  );
 
 
  /* ==========================================================
     INITIAL LOAD
     ========================================================== */
 
  if (document.readyState === 'loading') {
 
    document.addEventListener(
      'DOMContentLoaded',
      initAll
    );
 
  } else {
 
    initAll();
 
  }
 
 
  /* ==========================================================
     SHOPIFY AJAX / PRODUCT GRID RE-RENDER
     ========================================================== */
 
  function observeProductGrid() {
    var grid =
      document.getElementById(
        'ProductGridContainer'
      );
 
    if (
      !grid ||
      !window.MutationObserver
    ) {
      return;
    }
 
 
    var observer =
      new MutationObserver(
        function () {
 
          /*
           * Re-apply state synchronously (inside the MutationObserver
           * callback, which the browser runs as a microtask) instead of
           * deferring to requestAnimationFrame.
           *
           * Shopify's renderProductGridContainer() replaces the whole
           * #ProductGridContainer innerHTML — including this Shop by
           * widget — on every filter click (e.g. picking a colour).
           * The fresh markup always comes back in its server-rendered
           * default state (no shopby--open class, panels hidden per the
           * Liquid defaults), not the mobile tab / accordion state the
           * shopper had open.
           *
           * requestAnimationFrame() explicitly waits for the *next*
           * paint, so the browser was painting that default state first,
           * then snapping to the correct open panel a frame later —
           * that snap is the flicker reported when selecting a colour.
           * Running initAll() here instead applies the correct classes
           * before the browser paints anything, so there's nothing
           * incorrect to flash.
           */
          initAll();
 
        }
      );
 
 
    observer.observe(
      grid,
      {
        childList: true,
        subtree: false
      }
    );
  }
 
 
  observeProductGrid();
 
 
  /* ==========================================================
     RESPONSIVE CHANGE
     ========================================================== */
 
  var mobileMediaQuery =
    window.matchMedia(
      '(max-width: ' +
      MOBILE_BREAKPOINT +
      'px)'
    );
 
 
  function handleViewportChange() {
 
    document
      .querySelectorAll('.shopby')
      .forEach(function (root) {
 
        if (isMobile()) {
 
          /*
           * Whenever switching from desktop → mobile,
           * start compact / collapsed.
           */
          currentMobileTab = null;
 
          collapseMobile(root);
 
        } else {
 
          /*
           * Switching mobile → desktop:
           * always restore a valid desktop panel.
           */
          var desktopTab =
            currentDesktopTab ||
            getMarkupDefaultTab(root) ||
            getFirstTab(root);
 
          setDesktopTab(
            root,
            desktopTab
          );
 
        }
      });
  }
 
 
  if (mobileMediaQuery.addEventListener) {
 
    mobileMediaQuery.addEventListener(
      'change',
      handleViewportChange
    );
 
  } else if (mobileMediaQuery.addListener) {
 
    /*
     * Older Safari fallback.
     */
    mobileMediaQuery.addListener(
      handleViewportChange
    );
  }
 
})();
 




